import Reveal from '@/components/base/Reveal';

export interface SetupPoint {
  title: string;
  detail: string;
}

interface SetupStepProps {
  id: string;
  index: string;
  kicker: string;
  title: string;
  intro: string;
  points: SetupPoint[];
  note: string;
  panelLabel: string;
  reverse?: boolean;
  tone?: 'plain' | 'muted';
}

function SetupStep({
  id,
  index,
  kicker,
  title,
  intro,
  points,
  note,
  panelLabel,
  reverse = false,
  tone = 'plain',
}: SetupStepProps) {
  return (
    <section
      id={id}
      className={`w-full scroll-mt-[76px] px-4 md:px-8 lg:px-12 py-16 md:py-24 ${
        tone === 'muted'
          ? 'bg-background-100 border-y border-background-200'
          : 'bg-background-50'
      }`}
    >
      <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[42fr_58fr] gap-10 lg:gap-16 items-start">
        <div className={`w-full ${reverse ? 'lg:order-2' : ''}`}>
          <Reveal>
            <span className="block font-data text-[64px] md:text-[80px] leading-none font-semibold text-primary-200 select-none">
              {index}
            </span>
            <div className="mt-5 flex items-center gap-3">
              <span className="text-[11px] font-data tracking-[0.24em] text-primary-600">
                {kicker}
              </span>
              <span className="w-8 h-px bg-primary-400"></span>
            </div>
            <h2 className="mt-4 font-heading font-extrabold tracking-tight text-foreground-950 text-[30px] leading-[1.1] sm:text-[40px] lg:text-[46px]">
              {title}
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-foreground-600 max-w-[480px]">
              {intro}
            </p>
          </Reveal>

          <Reveal delay={120}>
            <div className="mt-8 rounded-lg border border-primary-200 bg-primary-50 px-5 py-4 max-w-[480px]">
              <div className="flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-information-line text-[16px] text-primary-700"></i>
                </span>
                <p className="text-[13px] leading-relaxed text-primary-900">{note}</p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className={`w-full ${reverse ? 'lg:order-1' : ''}`}>
          <Reveal delay={80}>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[11px] font-data tracking-[0.2em] text-foreground-500">
                {panelLabel}
              </span>
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
                <span className="text-[10px] font-data tracking-[0.2em] text-primary-600">
                  STEP {index}
                </span>
              </span>
            </div>

            <div className="rounded-lg border border-background-300 bg-background-50 overflow-hidden">
              <div className="px-4 md:px-5 divide-y divide-background-200">
                {points.map((point, idx) => (
                  <div key={point.title} className="flex items-start gap-4 py-5">
                    <span className="font-data text-[12px] tracking-wider text-primary-600 pt-0.5 shrink-0">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <h3 className="font-heading font-bold tracking-tight text-foreground-950 text-[16px]">
                        {point.title}
                      </h3>
                      <p className="mt-1.5 text-[13px] leading-relaxed text-foreground-600">
                        {point.detail}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default SetupStep;